/**
 * Darstellung der Vorschaltgeräte (Betriebsgeräte) für Beleuchtung 2.
 *
 * Eine Stelle für die Beschriftung, weil es zwei Verbraucher gibt: die Auswahlliste in
 * `Lighting2Form.vue` und das Glossar in `Lighting2Help.vue`. Das Glossar war zuvor von
 * Hand gepflegt und schon auseinandergelaufen — es führte „PSR – regelbares,
 * elektronisches Betriebsgerät“, während der Datensatz „Regelbarer LED Treiber“ sagt.
 *
 * Die Objekte kommen unverändert aus `/methods/lighting2/ballast/`
 * (`LightingBallastSerializer`).
 */

export interface Ballast {
  id: number
  /** Kürzel wie auf dem Datenblatt, z. B. „KVG“. */
  name: string
  /** Ausgeschriebener Gerätename. */
  full_name: string
  /** Gespeicherte Verlustleistung je Gerät in W. */
  power: number
  /** Verlustleistung, die die Rechnung tatsächlich ansetzt — 0 für LED-Treiber. */
  effective_power: number
  included_in_lamp_power: boolean
  dimmable: boolean
  switchable: boolean
}

export const BALLAST_GROUP_LEGACY = 'Bestandsgeräte'
export const BALLAST_GROUP_CONTROLLABLE = 'Steuerbare Geräte (Leuchtensystemtausch)'

/** W ohne überflüssige Nachkommastelle: 13 statt 13,0. */
function formatWatt(value: number): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return Number.isInteger(n) ? String(n) : n.toLocaleString('de-DE')
}

/**
 * „KVG — Konventionelles Vorschaltgerät“.
 *
 * Das Kürzel bleibt vorn: es steht so auf den Datenblättern und ist das, wonach in der
 * Liste gesucht wird. Der ausgeschriebene Name stand bislang zwar in der Antwort des
 * Endpunkts, wurde aber nirgends angezeigt.
 */
export function ballastLabel(ballast: Ballast | null | undefined): string {
  if (!ballast) return ''
  const full = ballast.full_name
  if (!full || full === ballast.name) return ballast.name
  return `${ballast.name} — ${full}`
}

/**
 * Was das Gerät die Rechnung kostet, als fertiger Satz.
 *
 * Für LED-Treiber **keine Wattzahl**: der Datensatz führt zwar 4 W, die Rechnung addiert
 * sie ab Rechenstand 2 aber nicht mehr, weil LED-Leuchten mit ihrer Systemleistung
 * inklusive Treiber katalogisiert sind (Befund B1). Die gespeicherten 4 W hier
 * anzuzeigen wäre eine Zahl, mit der niemand rechnet.
 */
export function ballastPowerNote(ballast: Ballast | null | undefined): string {
  if (!ballast) return ''
  if (ballast.included_in_lamp_power) {
    return 'Treiberverlust bereits in der Leuchtenleistung enthalten — keine Zusatzleistung'
  }
  if (!ballast.power) return 'Keine Verlustleistung'
  return `Verlustleistung ${formatWatt(ballast.power)} W je Gerät`
}

/** Steuerbarkeit im Klartext — erklärt, welche Regelungen später wählbar sind. */
export function ballastControlNote(ballast: Ballast | null | undefined): string {
  if (!ballast) return ''
  if (ballast.dimmable) return 'Stufenlos regelbar — Tageslichtregelung möglich'
  if (ballast.switchable) return 'Nur schaltbar — Präsenzsteuerung ja, Tageslichtabschaltung nein'
  return 'Bestandsgerät ohne Steuerbarkeit'
}

/**
 * Gruppenüberschrift für die Auswahlliste.
 *
 * Aus den Daten abgeleitet, nicht aus einer Namensliste: „Beide `False` — Bestandsgerät
 * (–, EVG, VVG, KVG)“ steht so am Modell. Ein neues steuerbares Gerät landet damit von
 * selbst in der richtigen Gruppe.
 */
export function ballastGroup(ballast: Ballast | null | undefined): string {
  return ballast && (ballast.dimmable || ballast.switchable)
    ? BALLAST_GROUP_CONTROLLABLE
    : BALLAST_GROUP_LEGACY
}

/**
 * Verlustleistung je Leuchte in W: Gerät × Geräte je Leuchte.
 *
 * Rechnet auf `effective_power`, nicht auf `power` — sonst stünde für eine LED-Leuchte
 * „8 W je Leuchte“, während die Rechnung 0 ansetzt. Der Server leitet das Feld aus
 * `included_in_lamp_power` ab, damit die Regel nicht doppelt gepflegt wird.
 */
export function ballastLossPerLuminaire(
  ballast: Ballast | null | undefined,
  ballastPerLamp: number | null | undefined,
): number {
  if (!ballast) return 0
  const each = Number(ballast.effective_power ?? 0)
  const count = Number(ballastPerLamp ?? 0)
  if (!Number.isFinite(each) || !Number.isFinite(count)) return 0
  return each * count
}

/**
 * Der Satz unter „Vorschaltgeräte je Leuchte“: was die Wahl real kostet.
 *
 * `amount` ist die Anzahl der Leuchten; fehlt sie, bleibt es bei der Angabe je Leuchte.
 */
export function ballastLossSummary(
  ballast: Ballast | null | undefined,
  ballastPerLamp: number | null | undefined,
  amount?: number | null,
): string {
  if (!ballast) return ''
  if (ballast.included_in_lamp_power) {
    return 'Der Treiber ist in der Leuchtenleistung enthalten und wird nicht zusätzlich angesetzt.'
  }

  const perLuminaire = ballastLossPerLuminaire(ballast, ballastPerLamp)
  if (!perLuminaire) return ''

  const each = formatWatt(ballast.effective_power)
  const count = Number(ballastPerLamp)
  const head = `${each} W × ${count} ${count === 1 ? 'Gerät' : 'Geräte'} = `
    + `${formatWatt(perLuminaire)} W je Leuchte`

  const luminaires = Number(amount)
  if (!Number.isFinite(luminaires) || luminaires <= 0) return head

  const totalKw = (perLuminaire * luminaires) / 1000
  return `${head}, bei ${luminaires.toLocaleString('de-DE')} Leuchten `
    + `${totalKw.toLocaleString('de-DE', { maximumFractionDigits: 3 })} kW Verlustleistung`
}
